import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';

const MINIMUM_FONT_SIZE = '0.875rem';

const portalTypography = {
    h1: {
        fontSize: '2rem'
    },
    h2: {
        fontSize: '1.75rem'
    },
    h3: {
        fontSize: '1.5rem'
    },
    h4: {
        fontSize: '1.25rem'
    },
    h5: {
        fontSize: '1.125rem'
    },
    h6: {
        fontSize: MINIMUM_FONT_SIZE
    },
    subtitle1: {
        fontSize: '1rem'
    },
    subtitle2: {
        fontSize: MINIMUM_FONT_SIZE
    },
    subtitle3: {
        fontSize: MINIMUM_FONT_SIZE
    },
    body1: {
        fontSize: '1rem'
    },
    body2: {
        fontSize: MINIMUM_FONT_SIZE
    },
    caption: {
        fontSize: MINIMUM_FONT_SIZE,
        lineHeight: 1
    },
    overline: {
        fontSize: MINIMUM_FONT_SIZE
    }
};

const portalComponents = {
    MuiAlert: {
        styleOverrides: {
            message: {
                fontSize: '1rem',
                lineHeight: 1.5
            }
        }
    },
    MuiBadge: {
        styleOverrides: {
            badge: {
                fontSize: MINIMUM_FONT_SIZE,
                minWidth: 22,
                height: 22
            }
        }
    },
    MuiButton: {
        styleOverrides: {
            root: {
                fontSize: MINIMUM_FONT_SIZE,
                lineHeight: 1.5,
                minHeight: 40
            }
        }
    },
    MuiCardHeader: {
        styleOverrides: {
            title: {
                fontSize: '1.25rem',
                lineHeight: 1.4
            },
            subheader: {
                fontSize: MINIMUM_FONT_SIZE,
                lineHeight: 1.5
            }
        }
    },
    MuiChip: {
        styleOverrides: {
            root: {
                maxWidth: '100%',
                minHeight: 32,
                height: 'auto'
            },
            label: {
                fontSize: MINIMUM_FONT_SIZE,
                lineHeight: 1.4,
                paddingTop: 4,
                paddingBottom: 4
            }
        }
    },
    MuiDialogContentText: {
        styleOverrides: {
            root: {
                fontSize: '1rem',
                lineHeight: 1.5
            }
        }
    },
    MuiDialogTitle: {
        styleOverrides: {
            root: {
                fontSize: '1.25rem',
                lineHeight: 1.4
            }
        }
    },
    MuiFormControlLabel: {
        styleOverrides: {
            label: {
                fontSize: MINIMUM_FONT_SIZE,
                lineHeight: 1.5
            }
        }
    },
    MuiFormHelperText: {
        styleOverrides: {
            root: {
                fontSize: MINIMUM_FONT_SIZE,
                lineHeight: 1
            }
        }
    },
    MuiInputBase: {
        styleOverrides: {
            root: {
                fontSize: '1rem',
                lineHeight: 1.5
            },
            input: {
                '&::placeholder': {
                    fontSize: '1rem'
                }
            }
        }
    },
    MuiInputLabel: {
        styleOverrides: {
            root: {
                fontSize: '1rem',
                lineHeight: 1.5
            }
        }
    },
    MuiMenuItem: {
        styleOverrides: {
            root: {
                fontSize: '1rem',
                lineHeight: 1.5,
                whiteSpace: 'normal'
            }
        }
    },
    MuiPaginationItem: {
        styleOverrides: {
            root: {
                fontSize: MINIMUM_FONT_SIZE
            }
        }
    },
    MuiSnackbarContent: {
        styleOverrides: {
            message: {
                fontSize: '1rem',
                lineHeight: 1.5
            }
        }
    },
    MuiTab: {
        styleOverrides: {
            root: {
                fontSize: '1rem',
                lineHeight: 1.5,
                minHeight: 48
            }
        }
    },
    MuiTableCell: {
        styleOverrides: {
            root: {
                fontSize: '1rem',
                lineHeight: 1.5
            },
            head: {
                fontSize: '1rem'
            }
        }
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: {
                fontSize: MINIMUM_FONT_SIZE,
                lineHeight: 1.5
            }
        }
    }
};

/**
 * Applies the shared Candidate and Guest Portal typography scale without affecting other portals
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Candidate or Guest-facing route content
 * @returns {JSX.Element}
 */
export default function PortalThemeProvider({ children }) {
    const baseTheme = useTheme();
    const portalTheme = useMemo(
        () =>
            createTheme(baseTheme, {
                portal: {
                    minimumFontSize: MINIMUM_FONT_SIZE
                },
                typography: portalTypography,
                components: portalComponents
            }),
        [baseTheme]
    );

    return <ThemeProvider theme={portalTheme}>{children}</ThemeProvider>;
}

PortalThemeProvider.propTypes = {
    children: PropTypes.node
};
